// Same idea as trimEdgeWhitespace on the kiosk settings page: a space in a
// coordinate is always a copy-paste typo, and the offload map can't parse a
// value saved with a leading space. A coordinate never legitimately contains
// whitespace, so we strip it everywhere — that also mends "- 59.3" into
// "-59.3". The only sign kept is a leading minus; a typographic minus
// (U+2212) or dash pasted from a document or web page becomes "-". A lone
// decimal comma (Swedish/European keyboards) becomes a dot.
export function normalizeCoordinate(value: string | null | undefined): string {
  if (!value) return "";
  const v = value.replace(/\s+/g, "").replace(/^[−‒–—]/, "-");
  return /^-?\d+,\d+$/.test(v) ? v.replace(",", ".") : v;
}

export type CoordinateAxis = "latitude" | "longitude";

const LIMIT: Record<CoordinateAxis, number> = { latitude: 90, longitude: 180 };

// Plain decimal degrees only — what the offload map and Google Maps link both
// parse. No degree signs, N/S/E/W suffixes, exponents or thousands separators.
const DECIMAL_DEGREES = /^-?\d{1,3}(\.\d+)?$/;

/** Validation message for one coordinate, or null when it's empty or valid. Pass a normalized value. */
export function coordinateError(value: string | null | undefined, axis: CoordinateAxis): string | null {
  if (!value) return null;
  const limit = LIMIT[axis];
  if (!DECIMAL_DEGREES.test(value)) return `Enter the ${axis} in decimal degrees, e.g. ${axis === "latitude" ? "59.3293" : "18.0686"}.`;
  if (Math.abs(Number(value)) > limit) return `${axis === "latitude" ? "Latitude" : "Longitude"} must be between -${limit} and ${limit}.`;
  return null;
}

/** Errors for the latitude/longitude pair: each must be valid, and both or neither must be filled in. */
export function coordinatePairErrors(latitude: string | null | undefined, longitude: string | null | undefined): { latitude: string | null; longitude: string | null } {
  const errors = { latitude: coordinateError(latitude, "latitude"), longitude: coordinateError(longitude, "longitude") };
  if (!latitude && longitude) errors.latitude ??= "Latitude is required when longitude is set.";
  if (latitude && !longitude) errors.longitude ??= "Longitude is required when latitude is set.";
  if (!errors.latitude && !errors.longitude && latitude && longitude && Number(latitude) === 0 && Number(longitude) === 0) {
    errors.latitude = "0, 0 is not a real location — clear both fields or enter the actual coordinates.";
  }
  return errors;
}
